import type { BodyPart, VideoSubmissions } from "@/api/api.types";
import { Modal } from "@/ui";
import { tw } from "@/utils";

// Mirrors the backend's MOBILE_APP_TROUBLESHOOTING_TIPS_THRESHOLD default; highlights movements
// that have reached this many uploads and still haven't been processed successfully.
const HIGHLIGHT_THRESHOLD = 3;

const BODY_PART_LABELS: Record<BodyPart, string> = {
  LeftThumb: "Left thumb",
  RightThumb: "Right thumb",
  LeftPinkyFinger: "Left pinky finger",
  RightPinkyFinger: "Right pinky finger",
  LeftElbow: "Left elbow",
  RightElbow: "Right elbow",
  LeftKnee: "Left knee",
  RightKnee: "Right knee",
  ForwardFlexion: "Forward flexion",
};

interface VideoSubmissionsModalProps {
  show: boolean;
  onClose: () => void;
  patientName?: string;
  videoSubmissions: VideoSubmissions;
}

export const VideoSubmissionsModal = ({
  show,
  onClose,
  patientName,
  videoSubmissions,
}: VideoSubmissionsModalProps) => (
  <Modal
    title="Video submissions"
    description={`${
      patientName
        ? `Videos ${patientName} submitted for each movement in their latest assessment.`
        : "Videos submitted for each movement in the latest assessment."
    } Movements in green were processed successfully.`}
    show={show}
    onClose={onClose}
  >
    <ul className="divide-y divide-gray-200 px-6 pb-6">
      {(Object.keys(BODY_PART_LABELS) as BodyPart[]).map((bodyPart) => {
        const count = videoSubmissions.byBodyPart[bodyPart] ?? 0;
        const isProcessed =
          videoSubmissions.processedBodyParts.includes(bodyPart);

        return (
          <li
            key={bodyPart}
            className={tw(
              "flex justify-between px-2 py-2 text-sm",
              isProcessed && "bg-green-50 text-green-800",
            )}
          >
            <span>
              {BODY_PART_LABELS[bodyPart]}
              {isProcessed && <span className="sr-only"> (processed)</span>}
            </span>
            <span
              className={tw(
                !isProcessed &&
                  count >= HIGHLIGHT_THRESHOLD &&
                  "font-semibold text-violet-900",
              )}
            >
              {count}
            </span>
          </li>
        );
      })}
      <li className="flex justify-between px-2 py-2 text-sm font-semibold">
        <span>Total</span>
        <span>{videoSubmissions.total}</span>
      </li>
    </ul>
  </Modal>
);
